/**
 * Create copywriting modal
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, TreeSelect, message } from 'antd';
import { copywritingApi, folderApi } from '../../services/resourceService';
import { ResourceScope, ResourceFolder, FolderType } from '../../types/resource';

const { TextArea } = Input;
const { Option } = Select;

interface CreateCopywritingModalProps {
  visible: boolean;
  scope: ResourceScope;
  onCancel: () => void;
  onSuccess: () => void;
}

const CreateCopywritingModal: React.FC<CreateCopywritingModalProps> = ({
  visible,
  scope,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState<ResourceFolder[]>([]);

  // Load folders
  useEffect(() => {
    if (visible) {
      loadFolders();
    }
  }, [visible, scope]);

  const loadFolders = async () => {
    try {
      const result = await folderApi.getTree(FolderType.COPYWRITING, scope);
      setFolders(result);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  // Convert folders to tree data
  const buildTreeData = (folders: ResourceFolder[]): any[] => {
    return folders.map((folder) => ({
      title: folder.name,
      value: folder.id,
      children: folder.children ? buildTreeData(folder.children) : [],
    }));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await copywritingApi.create({
        title: values.title,
        content: values.content,
        folder_id: values.folder_id || null,
        scope: scope,
        tags: values.tags ? values.tags.split(',').map((t: string) => t.trim()) : [],
      });

      message.success('创建成功');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error(error.message || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="新增文案"
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="title"
          label="标题"
          rules={[{ required: true, message: '请输入标题' }]}
        >
          <Input placeholder="请输入文案标题" />
        </Form.Item>

        <Form.Item
          name="content"
          label="内容"
          rules={[{ required: true, message: '请输入内容' }]}
        >
          <TextArea
            rows={6}
            placeholder="请输入文案内容"
            showCount
            maxLength={5000}
          />
        </Form.Item>

        <Form.Item name="folder_id" label="分组">
          <TreeSelect
            placeholder="请选择分组（可选）"
            allowClear
            treeData={buildTreeData(folders)}
          />
        </Form.Item>

        <Form.Item name="tags" label="标签">
          <Input placeholder="多个标签用逗号分隔" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateCopywritingModal;
